import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  minRows?: number;
  showAnimatedPlaceholder?: boolean;
}

const PLACEHOLDER_EXAMPLES = [
  "Create a landing page for my SaaS product...",
  "Show me my running applications...",
  "Set up a PostgreSQL database for my app...",
  "Deploy my React app to production...",
  "What containers are running on my account?",
  "Help me build a REST API with authentication...",
  "Create a new project from the blog template...",
  "Check the logs for my web application...",
];

export function ChatInput({
  onSend,
  disabled,
  minRows = 1,
  showAnimatedPlaceholder = false
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Animated placeholder effect
  useEffect(() => {
    if (!showAnimatedPlaceholder || message) return;

    const currentExample = PLACEHOLDER_EXAMPLES[placeholderIndex];
    let charIndex = 0;
    setIsTyping(true);
    setDisplayedPlaceholder('');

    // Type out the placeholder
    const typeInterval = setInterval(() => {
      if (charIndex <= currentExample.length) {
        setDisplayedPlaceholder(currentExample.slice(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);

        // Wait then move to next example
        setTimeout(() => {
          setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_EXAMPLES.length);
        }, 3000);
      }
    }, 50);

    return () => clearInterval(typeInterval);
  }, [placeholderIndex, showAnimatedPlaceholder, message]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const minHeight = minRows * 24 + 24; // Approximate line height + padding
      textarea.style.height = `${Math.max(Math.min(textarea.scrollHeight, 200), minHeight)}px`;
    }
  }, [message, minRows]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = message.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const placeholder = showAnimatedPlaceholder && !message
    ? displayedPlaceholder + (isTyping ? '|' : '')
    : "Message WiserWeb AI...";

  return (
    <form onSubmit={handleSubmit} className="relative">
      <div className="relative flex items-end gap-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={minRows}
            className={cn(
              'w-full chat-input px-4 py-3 pr-12 resize-none',
              'placeholder:text-zinc-500',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'focus:outline-none'
            )}
          />
          <div className="absolute right-2 bottom-2 flex items-center gap-1 text-xs text-zinc-600">
            <kbd className="px-1.5 py-0.5 bg-zinc-800 rounded text-zinc-500">
              Enter
            </kbd>
            <span>to send</span>
          </div>
        </div>

        <button
          type="submit"
          disabled={disabled || !message.trim()}
          className={cn(
            'flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200',
            message.trim() && !disabled
              ? 'bg-gradient-primary text-white hover:opacity-90'
              : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
          )}
        >
          {disabled ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
          ) : (
            <span className="material-icons">send</span>
          )}
        </button>
      </div>

      <p className="mt-2 text-xs text-zinc-600 text-center">
        WiserWeb AI can make mistakes. Verify important actions.
      </p>
    </form>
  );
}
